import type { CommandValidationResult } from 'aiControlledCommands/defineAiControlledCommand';
import { isTruthy } from '@ardoq/common-helpers';
import {
  TableMappingType,
  type IntegrationWorkspace,
} from '@ardoq/api-types/integrations';
import {
  findColumnProblem,
  findLockProblem,
  findReferencedTypeProblem,
  findSheetStructureProblem,
  findValueProblem,
  toColumnNames,
  toWorkspaceComponentTypes,
} from './mappingProblems';
import type {
  AutomapColumnMapping,
  AutomapColumnsContext,
  AutomapColumnsPayload,
  AutomapTable,
  ContextTable,
  ResolvedPayload,
  ResolvedTable,
} from './types';

type TableResolution =
  | { status: 'valid'; table: ResolvedTable }
  | { status: 'rejected'; reason: string };

const resolveWorkspace = (
  { id, name }: IntegrationWorkspace,
  { workspaces, canCreateWorkspace }: AutomapColumnsContext
): IntegrationWorkspace | null => {
  if (id === null) {
    const trimmedName = name.trim();
    return canCreateWorkspace && trimmedName
      ? { id: null, name: trimmedName }
      : null;
  }
  const existing = workspaces.find(workspace => workspace.id === id);
  return existing ? { id: existing.id, name: existing.name } : null;
};

const resolveColumnWorkspace = (
  mapping: AutomapColumnMapping,
  context: AutomapColumnsContext
): AutomapColumnMapping | null => {
  const { mappedTo } = mapping;
  if (mappedTo.columnType !== 'reference') {
    return mapping;
  }
  const targetWorkspace = resolveWorkspace(mappedTo.targetWorkspace, context);
  return targetWorkspace
    ? { ...mapping, mappedTo: { ...mappedTo, targetWorkspace } }
    : null;
};

const toRequestedTargetWorkspace = ({
  rowRepresentation,
  rootWorkspace,
  targetWorkspace,
}: AutomapTable): IntegrationWorkspace | null =>
  rowRepresentation === TableMappingType.REFERENCES
    ? (targetWorkspace ?? rootWorkspace)
    : null;

const toWorkspaceHint = ({ canCreateWorkspace }: AutomapColumnsContext) =>
  canCreateWorkspace
    ? "Copy an entry from 'workspaces', or use { id: null, name } for a new workspace."
    : "Copy an entry from 'workspaces'; new workspaces can't be created.";

const resolveTable = (
  table: AutomapTable,
  contextTable: ContextTable,
  context: AutomapColumnsContext
): TableResolution => {
  const reject = (reason: string): TableResolution => ({
    status: 'rejected',
    reason: `Sheet '${contextTable.name}': ${reason}`,
  });

  const rootWorkspace = resolveWorkspace(table.rootWorkspace, context);
  if (!rootWorkspace) {
    return reject(`Invalid rootWorkspace. ${toWorkspaceHint(context)}`);
  }

  if (
    table.rowRepresentation === TableMappingType.COMPONENTS &&
    table.targetWorkspace
  ) {
    return reject('targetWorkspace must be null when rows are components.');
  }

  const requestedTargetWorkspace = toRequestedTargetWorkspace(table);
  const targetWorkspace = requestedTargetWorkspace
    ? resolveWorkspace(requestedTargetWorkspace, context)
    : null;
  if (requestedTargetWorkspace && !targetWorkspace) {
    return reject(`Invalid targetWorkspace. ${toWorkspaceHint(context)}`);
  }

  const { mappings, rowRepresentation } = table;
  const problems = [
    findColumnProblem({
      mappings,
      columns: contextTable.columns,
      rowRepresentation,
    }),
    findLockProblem({
      mappings,
      rowRepresentation,
      columns: contextTable.columns,
      lockedRowRepresentation: contextTable.lockedRowRepresentation,
      lockedColumns: contextTable.lockedColumns,
    }),
    findSheetStructureProblem(mappings, rowRepresentation),
    findValueProblem({
      mappings,
      componentTypes: toWorkspaceComponentTypes(rootWorkspace, context),
      canCreateTypes: context.canCreateTypes,
      fields: context.fields,
    }),
  ].filter(isTruthy);
  if (problems.length > 0) {
    return reject(problems.join(' '));
  }

  const resolvedMappings = mappings.map(mapping =>
    resolveColumnWorkspace(mapping, context)
  );
  const invalidWorkspaceColumns = mappings.filter(
    (_, position) => !resolvedMappings[position]
  );
  if (invalidWorkspaceColumns.length > 0) {
    return reject(
      `Invalid targetWorkspace for columns: ${toColumnNames(invalidWorkspaceColumns)}. ${toWorkspaceHint(context)}`
    );
  }

  const validMappings = resolvedMappings.filter(isTruthy);
  const referencedTypeProblem = findReferencedTypeProblem(
    validMappings,
    { rootWorkspace, targetWorkspace },
    context
  );
  if (referencedTypeProblem) {
    return reject(referencedTypeProblem);
  }

  return {
    status: 'valid',
    table: {
      id: contextTable.id,
      name: contextTable.name,
      index: contextTable.index,
      sourceFieldNames: contextTable.sourceFieldNames,
      rowRepresentation,
      rootWorkspace,
      targetWorkspace,
      mappings: validMappings.map(mapping =>
        withSheetColumnName(mapping, contextTable.columns)
      ),
      canCreateComponents: context.canCreateTypes,
    },
  };
};

const withSheetColumnName = (
  mapping: AutomapColumnMapping,
  columns: string[]
): AutomapColumnMapping => ({
  ...mapping,
  columnName: columns[mapping.columnIndex],
});

const rejectUnknownTable = ({ tableId }: AutomapTable): TableResolution => ({
  status: 'rejected',
  reason: `Unknown tableId '${tableId}'. Use an 'id' from 'tables'.`,
});

const isDuplicateTable = (
  { tableId }: AutomapTable,
  position: number,
  tables: AutomapTable[]
): boolean => tables.findIndex(table => table.tableId === tableId) !== position;

export const resolvePayload = (
  { tables }: AutomapColumnsPayload,
  context: AutomapColumnsContext
): CommandValidationResult<ResolvedPayload> => {
  const duplicateTables = tables.filter(isDuplicateTable);
  if (duplicateTables.length > 0) {
    return {
      status: 'rejected',
      reason: `Sheets listed more than once: ${duplicateTables.map(({ tableId }) => tableId).join(', ')}. List each sheet once.`,
    };
  }

  const resolutions = tables.map(table => {
    const contextTable = context.tables.find(({ id }) => id === table.tableId);
    if (!contextTable) {
      return rejectUnknownTable(table);
    }
    return resolveTable(table, contextTable, context);
  });
  const rejectionReasons = resolutions.flatMap(resolution =>
    resolution.status === 'rejected' ? [resolution.reason] : []
  );
  if (rejectionReasons.length > 0) {
    return { status: 'rejected', reason: rejectionReasons.join('\n') };
  }

  return {
    status: 'valid',
    payload: {
      integrationId: context.integrationId,
      tables: resolutions.flatMap(resolution =>
        resolution.status === 'valid' ? [resolution.table] : []
      ),
    },
  };
};
